import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CForm,
    CFormInput,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from "@coreui/react";

import { deleteReport, fetchReports, uploadReport } from "../api/reports";
import { t } from "../i18n";

export default function ReportsPage() {
    const navigate = useNavigate();

    const [reports, setReports] = useState([]);
    const [title, setTitle] = useState("");
    const [file, setFile] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [deletingReportId, setDeletingReportId] = useState(null);

    useEffect(() => {
        let isMounted = true;

        async function loadReports() {
            try {
                const loadedReports = await fetchReports();

                if (isMounted) {
                    setReports(loadedReports);
                    setError(null);
                }
            } catch (loadError) {
                if (isMounted) {
                    setError(loadError.message);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        loadReports();

        return () => {
            isMounted = false;
        };
    }, []);

    async function handleSubmit(event) {
        event.preventDefault();

        setError(null);
        setSubmitting(true);

        try {
            const report = await uploadReport({
                title: title.trim() || file.name,
                file,
            });

            setReports((currentReports) => [report, ...currentReports]);
            setTitle("");
            setFile(null);
            event.target.reset();
            navigate(`/reports/${report.id}`);
        } catch (uploadError) {
            setError(uploadError.message);
        } finally {
            setSubmitting(false);
        }
    }

    async function destroyReport(reportId) {
        if (!window.confirm(t("reports.confirmDelete"))) {
            return;
        }

        setError(null);
        setDeletingReportId(reportId);

        try {
            await deleteReport(reportId);

            setReports((currentReports) =>
                currentReports.filter((report) => report.id !== reportId)
            );
        } catch (destroyError) {
            setError(destroyError.message);
        } finally {
            setDeletingReportId(null);
        }
    }

    const canSubmit = file !== null && !submitting;

    return (
        <>
            <div className="mb-4">
                <h1>{t("reports.title")}</h1>
                <p className="text-body-secondary mb-0">
                    {t("reports.description")}
                </p>
            </div>

            <CCard className="mb-4">
                <CCardHeader>
                    <strong>{t("reports.upload")}</strong>
                </CCardHeader>

                <CCardBody>
                    {error && (
                        <CAlert color="danger">
                            {error}
                        </CAlert>
                    )}

                    <CForm onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <CFormInput
                                label={t("reports.titleLabel")}
                                placeholder={t("reports.titlePlaceholder")}
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <CFormInput
                                label={t("reports.fileLabel")}
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(event) => setFile(event.target.files[0] || null)}
                                required
                            />
                        </div>

                        <CButton color="primary" type="submit" disabled={!canSubmit}>
                            {submitting ? t("reports.uploading") : t("reports.uploadButton")}
                        </CButton>
                    </CForm>
                </CCardBody>
            </CCard>

            <CCard>
                <CCardHeader>
                    <strong>{t("reports.list")}</strong>
                </CCardHeader>

                <CCardBody>
                    {loading && (
                        <div className="d-flex align-items-center gap-2 text-body-secondary">
                            <CSpinner size="sm" />
                            {t("reports.loading")}
                        </div>
                    )}

                    {!loading && reports.length === 0 && (
                        <p className="text-body-secondary mb-0">
                            {t("reports.empty")}
                        </p>
                    )}

                    {!loading && reports.length > 0 && (
                        <CTable responsive hover className="mb-0 align-middle">
                            <CTableHead>
                                <CTableRow>
                                    <CTableHeaderCell scope="col">{t("reports.table.title")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.table.rows")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.table.assignedRows")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col">{t("reports.table.status")}</CTableHeaderCell>
                                    <CTableHeaderCell scope="col" className="text-end">
                                        {t("reports.table.actions")}
                                    </CTableHeaderCell>
                                </CTableRow>
                            </CTableHead>

                            <CTableBody>
                                {reports.map((report) => {
                                    const isDeleting = deletingReportId === report.id;

                                    return (
                                        <CTableRow key={report.id}>
                                            <CTableDataCell className="fw-semibold">
                                                {report.title}
                                            </CTableDataCell>
                                            <CTableDataCell>{report.rowCount}</CTableDataCell>
                                            <CTableDataCell>
                                                {report.assignedRowCount}/{report.rowCount}
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                {report.processed ? t("reports.processed") : t("reports.processing")}
                                            </CTableDataCell>
                                            <CTableDataCell className="text-end">
                                                <div className="d-flex justify-content-end gap-2">
                                                    <CButton
                                                        as={Link}
                                                        color="secondary"
                                                        variant="outline"
                                                        to={`/reports/${report.id}`}
                                                        disabled={isDeleting}
                                                    >
                                                        {t("reports.view")}
                                                    </CButton>

                                                    <CButton
                                                        color="danger"
                                                        variant="outline"
                                                        type="button"
                                                        disabled={isDeleting}
                                                        onClick={() => destroyReport(report.id)}
                                                    >
                                                        {isDeleting ? t("reports.deleting") : t("reports.delete")}
                                                    </CButton>
                                                </div>
                                            </CTableDataCell>
                                        </CTableRow>
                                    );
                                })}
                            </CTableBody>
                        </CTable>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
}
